import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import segnalaMancanti from '../../../components/Util/campiMancanti';
import { CampoRicerca } from '../../../components/Util/CampoRicerca';
import { usePageTitle } from '../../../layouts/PageHeaderExtra';
import { useStatoNelTitolo } from '../../../components/Util/StatoProdotto';
import { PAGE_META } from '../../../layouts/pageMeta';
import { RiquadroParlante } from '../../../components/PaginaParlante/ColonnaParlante';
import {
    STATI_PAGINA,
    aggiornaPaginaParlante,
    creaPaginaParlante,
    eliminaPaginaParlante,
    leggiPaginaParlante,
    pulisciUrl,
} from '../../../../api-client/pagineParlanti';

/*
 * Scheda di una pagina parlante: titolo del riquadro, pagina della Struttura dove compare e testo
 * (editor visuale). A destra l'anteprima della colonna come la vedrà la struttura.
 * Lo stato (Pubblicata / Bozza) sta accanto al titolo e si salva subito; le bozze non si vedono.
 */

const LIST_URL = '/superadmin/pagine-parlanti';

// Le pagine della Struttura che si possono scegliere: quelle con un'intestazione in pageMeta.js, tranne le
// vecchie pagine che il menu non apre piu' (`nonPiuUsata`)
const PAGINE_STRUTTURA = PAGE_META
    .filter((m) => m.match.startsWith('struttura/') && !m.nonPiuUsata)
    .map((m) => {
        const sezione = (m.crumbs || []).filter((c) => typeof c === 'string' && c !== 'Dashboard' && c !== m.title).join(' › ');
        return { value: m.match, label: m.title, dettaglio: `${sezione ? `${sezione} · ` : ''}/${m.match}` };
    });

export const nomePagina = (url) => PAGINE_STRUTTURA.find((p) => p.value === url)?.label || `/${url}`;

const VUOTA = { title: '', url: '', html: '' };

export const SchedaPaginaParlante = () => {
    const { id } = useParams();
    const isNew = id === 'nuova';
    const navigate = useNavigate();

    const [pagina, setPagina] = useState(null);
    const [form, setForm] = useState(VUOTA);
    const [caricamento, setCaricamento] = useState(!isNew);
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState('');
    const [confermaElimina, setConfermaElimina] = useState(false);
    const [salvato, setSalvato] = useState(false);

    useEffect(() => {
        if (isNew) { setPagina(null); setForm(VUOTA); setCaricamento(false); return; }
        let vivo = true;
        setCaricamento(true);
        leggiPaginaParlante(id).then((r) => {
            if (!vivo) return;
            setCaricamento(false);
            if (!r || r.error) { setErrore(r?.error || 'Pagina parlante non trovata.'); return; }
            setPagina(r);
            setForm({ title: r.title || '', url: r.url || '', html: r.html || '' });
        });
        return () => { vivo = false; };
    }, [id, isNew]);

    // Titolo della pagina = titolo della pagina parlante (regola SUI)
    usePageTitle(pagina?.title);

    // Avviso «Modifica salvata» in basso a sinistra per 2,5 secondi
    useEffect(() => {
        if (!salvato) return undefined;
        const t = setTimeout(() => setSalvato(false), 2500);
        return () => clearTimeout(t);
    }, [salvato]);

    // Stato accanto al titolo: tendina che salva subito (regola SUI)
    useStatoNelTitolo({
        visibile: !!pagina,
        stati: STATI_PAGINA,
        valore: pagina?.status,
        onCambia: async (status) => {
            const r = await aggiornaPaginaParlante(pagina.id, { status });
            if (r?.error) return { error: r.error };
            setPagina(r);
            setSalvato(true);
            return {};
        },
    });

    const opzioni = useMemo(() => {
        const note = PAGINE_STRUTTURA.some((p) => p.value === form.url);
        return note || !form.url ? PAGINE_STRUTTURA : [{ value: form.url, label: `/${form.url}`, dettaglio: 'Indirizzo non più tra le pagine della Struttura' }, ...PAGINE_STRUTTURA];
    }, [form.url]);

    // Dati mancanti o sbagliati: si evidenziano i campi (set-ui «Dati mancanti»), senza finestra
    const controlla = () => {
        const mancanti = [];
        if (!form.title.trim()) mancanti.push({ campo: 'pp-titolo', messaggio: 'Manca il titolo: è la prima riga del riquadro a destra' });
        if (!pulisciUrl(form.url)) mancanti.push({ campo: 'pp-pagina', messaggio: 'Manca la pagina della Struttura dove mostrare il testo' });
        else if (!pulisciUrl(form.url).startsWith('struttura/')) mancanti.push({ campo: 'pp-pagina', messaggio: 'L\'indirizzo deve essere una pagina della Struttura (inizia con struttura/)' });
        if (!form.html.replace(/<[^>]*>/g, '').trim()) mancanti.push({ campo: 'pp-contenuto', messaggio: 'Manca il testo da mostrare nella colonna' });
        return segnalaMancanti(mancanti);
    };

    const salva = async (status) => {
        if (controlla() > 0) return;
        setInCorso(true);
        const r = isNew
            ? await creaPaginaParlante({ ...form, status })
            : await aggiornaPaginaParlante(pagina.id, { title: form.title, url: form.url, html: form.html });
        setInCorso(false);
        if (!r || r.error) { setErrore(r?.error || 'Salvataggio non riuscito.'); return; }
        setSalvato(true);
        if (isNew) navigate(`${LIST_URL}/${r.id}`, { replace: true });
        else setPagina(r);
    };

    const elimina = async () => {
        setConfermaElimina(false);
        setInCorso(true);
        const r = await eliminaPaginaParlante(pagina.id);
        setInCorso(false);
        if (r?.error) { setErrore(r.error); return; }
        navigate(LIST_URL);
    };

    const cambiato = !pagina || form.title !== (pagina.title || '') || form.url !== (pagina.url || '') || form.html !== (pagina.html || '');

    if (caricamento) {
        return <div className="text-center py-5"><Spinner animation="border" role="status" /></div>;
    }

    return (
        <>
            <Modal show={!!errore} onHide={() => setErrore('')} centered>
                <Modal.Header closeButton><Modal.Title>Controlla i dati</Modal.Title></Modal.Header>
                <Modal.Body>{errore}</Modal.Body>
            </Modal>
            <Modal show={confermaElimina} onHide={() => setConfermaElimina(false)} centered>
                <Modal.Header closeButton><Modal.Title>Eliminare la pagina parlante?</Modal.Title></Modal.Header>
                <Modal.Body>Eliminare «{pagina?.title}»? La colonna a destra di quella pagina sparisce per tutte le strutture.</Modal.Body>
                <Modal.Footer>
                    <Button className="btn btn-light btn-add" onClick={() => setConfermaElimina(false)}>Annulla</Button>
                    <Button className="btn btn-danger btn-add" onClick={elimina}>Elimina</Button>
                </Modal.Footer>
            </Modal>

            <Row className="g-4">
                <Col xl={9}>
                    <Card>
                        <Card.Body className="sid-card-body--spaced">
                            <Row className="g-4">
                                <Col md={6}>
                                    <Form.Label htmlFor="pp-titolo">Titolo del riquadro *</Form.Label>
                                    <Form.Control id="pp-titolo" value={form.title} maxLength={120} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                                </Col>
                                <Col md={6}>
                                    <Form.Label htmlFor="pp-pagina">Pagina della Struttura *</Form.Label>
                                    <CampoRicerca
                                        id="pp-pagina"
                                        opzioni={opzioni}
                                        valore={form.url}
                                        onChange={(url) => setForm({ ...form, url })}
                                        nessunRisultato="Nessuna pagina con questo nome"
                                    />
                                </Col>
                            </Row>

                            <div className="mt-4">
                            <Form.Label>Contenuto *</Form.Label>
                            <div id="pp-contenuto" className="custom-ekeditor sid-editor-alto">
                                <CKEditor
                                    editor={ClassicEditor}
                                    data={form.html}
                                    config={{ toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'blockQuote', 'undo', 'redo'] }}
                                    onChange={(event, editor) => {
                                        const html = editor.getData();
                                        setForm((f) => (f.html === html ? f : { ...f, html }));
                                    }}
                                />
                            </div>
                            </div>
                        </Card.Body>
                    </Card>
                    <div className="d-flex justify-content-between mt-4">
                        {pagina
                            ? <Button className="btn btn-outline-danger" onClick={() => setConfermaElimina(true)} disabled={inCorso}>Elimina</Button>
                            : <span />}
                        <div className="d-flex gap-4">
                            <Button className="btn btn-light" onClick={() => navigate(LIST_URL)}>Annulla</Button>
                            {isNew ? (
                                <>
                                    <Button className="btn btn-light" onClick={() => salva('DRAFT')} disabled={inCorso}>Salva bozza</Button>
                                    <Button className="btn btn-primary" onClick={() => salva('PUBLISHED')} disabled={inCorso}>Salva e pubblica</Button>
                                </>
                            ) : (
                                <Button className="btn btn-primary" onClick={() => salva()} disabled={inCorso || !cambiato}>Salva</Button>
                            )}
                        </div>
                    </div>
                </Col>
                {/* Larga come la colonna destra delle pagine della Struttura (col-xl-3) */}
                <Col xl={3}>
                    <div className="sid-parlante-anteprima">
                        <div className="sid-parlante-anteprima__etichetta">Anteprima della colonna destra</div>
                        {form.title || form.html
                            ? <RiquadroParlante title={form.title} html={form.html} />
                            : <div className="sid-parlante-anteprima__vuota">Scrivi titolo e testo: qui vedi come appariranno alla struttura.</div>}
                    </div>
                </Col>
            </Row>


            {salvato && <div className="sid-avviso-salvato" role="status">✓ Modifica salvata</div>}
        </>
    );
};

export default SchedaPaginaParlante;
